import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { isNotEqual } from 'vet-bones/utils';
import { requestStardogVoiceboxGenesis } from 'vet-bones/utils/voicebox';

import { notificationsActionCreators } from 'src/actions/notifications/actionCreators';
import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { UnwrapPromise } from 'src/constants/types';
import {
  CompetencyQuestion,
  CompetencyQuestionEvaluationTag,
} from 'src/constants/voicebox';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { setModelCompetencyQuestions } from 'src/containers/ProjectDiagram/utils/setModelCompetencyQuestions';
import { projectSelector } from 'src/selectors/project';
import { Project } from 'src/store/state/projects';
import { hasModelConcepts } from 'src/utils/hasModelConcepts';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { buildGenerateScoresParameters } from 'src/utils/voicebox/hooks/parameters';
import { requestGenerateQueriesInChunks } from 'src/utils/voicebox/hooks/requestGenerateQueriesInChunks';
import {
  GenerateScoresRequest,
  GenerateScoresResponse,
} from 'src/utils/voicebox/hooks/types';

export type EvaluateQuestionsContextType = {
  evaluateQuestions: (
    questions: CompetencyQuestion[],
    options?: {
      skipFailureNotification?: boolean;
      waitForQueries?: boolean;
      project?: Project;
    }
  ) => Promise<CompetencyQuestion[] | null>;
  isLoadingEvaluations: boolean;
  loadingEvaluationIds: Set<string>;
  isLoadingQueries: boolean;
  loadingQueryIds: Set<string>;
  waitForEvaluatedQuestions: () => Promise<CompetencyQuestion[] | null>;
};

export const EvaluateQuestionsContext =
  React.createContext<EvaluateQuestionsContextType>({
    evaluateQuestions: async () => null,
    isLoadingEvaluations: false,
    loadingEvaluationIds: new Set(),
    isLoadingQueries: false,
    loadingQueryIds: new Set(),
    waitForEvaluatedQuestions: async () => null,
  });

export const useEvaluateQuestions = () => {
  return React.useContext(EvaluateQuestionsContext);
};

export const useEvaluateQuestionsContextValue =
  (): EvaluateQuestionsContextType => {
    const { projectId } = useRouterParams();
    const diagram = useDiagram();
    const dispatch = useReduxDispatch();

    const defaultConnection = useDefaultPortalConnection();
    const maybeProject = useReduxSelector(projectSelector(projectId));

    const loadingKey = React.useRef('');
    const waitForEvaluatedQuestionsRef = React.useRef(false);
    const lastEvaluatedQuestionsRef = React.useRef<CompetencyQuestion[] | null>(
      null
    );

    const [isLoadingEvaluations, setIsLoadingEvaluations] =
      React.useState(false);
    const [loadingEvaluationIds, setLoadingEvaluationIds] = React.useState<
      Set<string>
    >(new Set());

    const [isLoadingQueries, setIsLoadingQueries] = React.useState(false);
    const [loadingQueryIds, setLoadingQueryIds] = React.useState<Set<string>>(
      new Set()
    );

    const evaluateQuestions = React.useCallback(
      async (
        questions: CompetencyQuestion[],
        options?: {
          skipFailureNotification?: boolean;
          waitForQueries?: boolean;
          project?: Project;
        }
      ): Promise<CompetencyQuestion[] | null> => {
        const project = options?.project || maybeProject;

        if (
          !defaultConnection ||
          !project ||
          !diagram ||
          !hasModelConcepts(project.diagram) ||
          !questions.length
        ) {
          return null;
        }

        const questionIds = questions.map((q) => q.id);
        const loadingQuestionIds = new Set(questionIds);

        const notifyFailedQueries = (failedQuestionIds: string[]) => {
          if (failedQuestionIds.length && !options?.skipFailureNotification) {
            dispatch(
              notificationsActionCreators.voiceboxGenerateQueriesFailed(
                failedQuestionIds
              )
            );
          }
        };

        const currentLoadingKey = uuid();
        loadingKey.current = currentLoadingKey;

        setIsLoadingEvaluations(true);
        setLoadingEvaluationIds(loadingQuestionIds);
        setIsLoadingQueries(true);
        setLoadingQueryIds(loadingQuestionIds);
        waitForEvaluatedQuestionsRef.current = true;
        lastEvaluatedQuestionsRef.current = null;

        let nextQuestions: CompetencyQuestion[] | null = null;
        let didEvaluationFail = false;

        try {
          const evaluationResponse = await requestStardogVoiceboxGenesis<
            GenerateScoresRequest,
            GenerateScoresResponse
          >({
            connectionIdx: defaultConnection.index,
            body: {
              endpoint: defaultConnection.endpoint,
              action: 'generate_scores',
              parameters: buildGenerateScoresParameters(project, questions),
            },
          });

          if (loadingKey.current !== currentLoadingKey) {
            return null;
          }

          if (evaluationResponse.error) {
            throw new Error(evaluationResponse.error);
          }

          const queriesRequest = requestGenerateQueriesInChunks({
            connectionIdx: defaultConnection.index,
            endpoint: defaultConnection.endpoint,
            isStale: () => loadingKey.current !== currentLoadingKey,
            project,
            questions,
          });

          let queriesResponse: UnwrapPromise<typeof queriesRequest> | undefined;
          if (options?.waitForQueries) {
            queriesResponse = await queriesRequest;

            if (loadingKey.current !== currentLoadingKey) {
              return null;
            }

            notifyFailedQueries(queriesResponse.failedQuestionIds);
          } else {
            queriesRequest
              .then((queriesResponse) => {
                if (loadingKey.current !== currentLoadingKey) {
                  return;
                }

                notifyFailedQueries(queriesResponse.failedQuestionIds);

                const currentQuestions =
                  diagram.model.modelData.competencyQuestions;
                const nextQuestionsWithQueries =
                  currentQuestions.map<CompetencyQuestion>((question) => {
                    if (!loadingQuestionIds.has(question.id)) {
                      return question;
                    }

                    const query = queriesResponse.results.questions.find(
                      (res) => res.id === question.id
                    );

                    return {
                      ...question,
                      evaluation: question.evaluation
                        ? {
                            ...question.evaluation,
                            query: query?.query || null,
                          }
                        : undefined,
                    };
                  });

                if (isNotEqual(currentQuestions, nextQuestionsWithQueries)) {
                  commitDiagramModel(
                    diagram,
                    setModelCompetencyQuestions(nextQuestionsWithQueries)
                  );
                }

                if (loadingKey.current === currentLoadingKey) {
                  lastEvaluatedQuestionsRef.current = nextQuestionsWithQueries;
                }
              })
              // The chunked request never rejects, so only the merge above can
              // throw here. Reset the ref for any waiter; this is not a query
              // failure, so it is not reported as one.
              .catch(() => {
                if (loadingKey.current !== currentLoadingKey) {
                  return;
                }

                lastEvaluatedQuestionsRef.current = [];
              })
              .finally(() => {
                if (loadingKey.current !== currentLoadingKey) {
                  return;
                }

                setIsLoadingQueries(false);
                setLoadingQueryIds(new Set());
                if (loadingKey.current === currentLoadingKey) {
                  waitForEvaluatedQuestionsRef.current = false;
                }
              });
          }

          const currentQuestions = diagram.model.modelData.competencyQuestions;
          nextQuestions = currentQuestions.map<CompetencyQuestion>(
            (question) => {
              if (!loadingQuestionIds.has(question.id)) {
                return question;
              }

              const evaluation = evaluationResponse.results?.questions.find(
                (res) => res.id === question.id
              );
              const query = queriesResponse?.results.questions.find(
                (res) => res.id === question.id
              );

              return {
                ...question,
                evaluation: evaluation
                  ? {
                      score: evaluation.score,
                      explanation: evaluation.explanation,
                      tags: evaluation.tags.map((tag) =>
                        tag.toUpperCase()
                      ) as CompetencyQuestionEvaluationTag[],
                      query: query?.query || null,
                    }
                  : undefined,
              };
            }
          );

          if (isNotEqual(currentQuestions, nextQuestions)) {
            commitDiagramModel(
              diagram,
              setModelCompetencyQuestions(nextQuestions)
            );
          }

          dispatch(
            projectsActionCreators.evaluatedProjectCompetencyQuestions(
              projectId,
              questionIds
            )
          );

          if (loadingKey.current === currentLoadingKey) {
            lastEvaluatedQuestionsRef.current = nextQuestions;
          }
        } catch (err) {
          didEvaluationFail = true;

          if (!options?.skipFailureNotification) {
            dispatch(
              notificationsActionCreators.voiceboxGenerateScoresFailed(
                questionIds
              )
            );
          }

          if (loadingKey.current === currentLoadingKey) {
            lastEvaluatedQuestionsRef.current = [];
          }
        }

        if (loadingKey.current !== currentLoadingKey) {
          return null;
        }

        setLoadingEvaluationIds(new Set());
        setIsLoadingEvaluations(false);

        if (didEvaluationFail || options?.waitForQueries) {
          setIsLoadingQueries(false);
          setLoadingQueryIds(new Set());
          if (loadingKey.current === currentLoadingKey) {
            waitForEvaluatedQuestionsRef.current = false;
          }
        }

        return nextQuestions;
      },
      [defaultConnection, diagram, dispatch, maybeProject, projectId]
    );

    const waitForEvaluatedQuestions = React.useCallback(async () => {
      return new Promise<CompetencyQuestion[] | null>((resolve) => {
        const checkIsLoading = () => {
          if (waitForEvaluatedQuestionsRef.current) {
            setTimeout(checkIsLoading, 100);
          } else {
            resolve(lastEvaluatedQuestionsRef.current);
          }
        };

        checkIsLoading();
      });
    }, []);

    return {
      evaluateQuestions,
      isLoadingEvaluations,
      loadingEvaluationIds,
      isLoadingQueries,
      loadingQueryIds,
      waitForEvaluatedQuestions,
    };
  };
